import { useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
	Box,
	Heading,
	Text,
	Button,
	VStack,
	Flex,
	Tag,
	HStack,
	useColorModeValue,
} from "@chakra-ui/react"
import { ArrowBackIcon } from "@chakra-ui/icons"
import { formatAddress } from "../../Helpers/BulkUploadCSVProcessor"

export const JobRequestBulkUploadConfirmation = () => {
	const location = useLocation()
	const navigate = useNavigate()
	const { results = [] } = location.state || {}

	// If no results were passed, redirect back to the upload page
	useEffect(() => {
		if (!results || results.length === 0) {
			navigate("/job-requests/bulk-upload")
		}
	}, [navigate, results])

	// If redirecting, return null
	if (!results || results.length === 0) {
		return null
	}

	// Count the different statuses
	const statusCounts = results.reduce((acc, result) => {
		acc[result.status] = (acc[result.status] || 0) + 1
		return acc
	}, {})

	return (
		<Box maxW="1000px" mx="auto" p={6}>
			<Heading size="lg" fontWeight="bold" mb="25px">
				Bulk Upload Work Order Confirmation
			</Heading>

			<Button
				leftIcon={<ArrowBackIcon />}
				variant="ghost"
				mb={6}
				pl={0}
				onClick={() => {
					navigate("/job-requests")
				}}
			>
				Work Orders
			</Button>

			<VStack spacing={4} align="stretch">
				{results.map((result) => (
					<ResultCard key={result.rowNumber} result={result} />
				))}
			</VStack>

			<Flex justifyContent="center" mt={8}>
				<Button
					onClick={() => navigate("/job-requests")}
					w="100%"
					size="lg"
					bgColor="borderColor"
					color="white"
				>
					View All Work Orders
				</Button>
			</Flex>
		</Box>
	)
}

// Individual result card component
const ResultCard = ({ result }) => {
	const bgColor = useColorModeValue("white", "gray.700")
	const borderColor = useColorModeValue("gray.200", "gray.600")

	// Status tag styling
	const getStatusTag = (status) => {
		switch (status) {
			case "complete":
				return (
					<Tag size="sm" colorScheme="green" borderRadius="full" px={3}>
						Complete
					</Tag>
				)
			case "failed":
				return (
					<Tag size="sm" colorScheme="red" borderRadius="full" px={3}>
						Failed
					</Tag>
				)
			case "partial":
				return (
					<Tag size="sm" colorScheme="yellow" borderRadius="full" px={3}>
						Partial
					</Tag>
				)
			default:
				return null
		}
	}

	// Use formattedAddress if available, otherwise format it here
	const addressDisplay =
		result.formattedAddress ||
		formatAddress(result.address, result.city, result.state, result.zip)

	return (
		<Box
			borderWidth="1px"
			borderRadius="lg"
			borderColor={borderColor}
			bg={bgColor}
			p={4}
			shadow="sm"
		>
			<Flex direction="column">
				<HStack justify="space-between" mb={2}>
					<Text fontWeight="bold">Row #{result.rowNumber}</Text>
					{getStatusTag(result.status)}
				</HStack>

				<Text fontSize="md">{addressDisplay}</Text>
				<Text fontSize="sm" color="gray.500" mb={2}>
					{result.serviceType}
				</Text>

				{result.status === "failed" && result.error && (
					<Box mt={2} p={2} bg="red.50" borderRadius="md">
						<Text color="red.500" fontSize="sm">
							{result.error}
						</Text>
					</Box>
				)}

				{result.status === "partial" && (
					<Box mt={2} p={2} bg="yellow.50" borderRadius="md">
						<Text color="yellow.600" fontSize="sm">
							Did not create job in PPW. You can view work order details in
							Quiplo.
						</Text>
					</Box>
				)}
			</Flex>
		</Box>
	)
}
